import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
  FormControl,
  Switch,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { usePartners } from '@/services/queries/partner';
import { GetParamsObject } from '@/utils';
import { useUpdateBannerMutation } from '@/services/queries/banner';
import { Banner } from '@/model';
import { UpdateBannerRequest } from '@/services/request/banner';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { SelectField } from '@/components/SelectField';

export interface BannerUpdateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Banner;
  onClose: () => void;
  onDone?: () => void;
}

export const BannerUpdateModal = ({ isOpen, onClose, onDone, item }: BannerUpdateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { data, isLoading: isLoadingPartners } = usePartners(GetParamsObject({}, 1, 900, 'asc', 'name'));

  const partners = data?.data || [];
  const updateBannerMutation = useUpdateBannerMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: item.title,
      file: undefined,
      source_url: item.source_url,
      start_at: item.start_at,
      end_at: item.end_at,
      is_scheduled: !!(item.start_at && item.end_at),
      has_partner: !!item.partner_uuid,
      partner_uuid: item.partner_uuid,
      team_uuid: item.team_uuid,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formValues = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: UpdateBannerRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.file,
        title: data.title,
        source_url: data.source_url,
        start_at: data.is_scheduled ? data.start_at || '' : '',
        end_at: data.is_scheduled ? data.end_at || '' : '',
        partner_uuid: data.partner_uuid || '',
        order: 0,
      };

      updateBannerMutation.mutate(
        { id: item.uuid, payload },
        {
          onSuccess: () => {
            onDone?.();
            reset();
            toast({
              status: 'success',
              title: t('pages.application_banner.components.banner_edit_modal.success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_banner.components.banner_edit_modal.error'),
            });
          },
        },
      );
    },
    [updateBannerMutation, item.uuid, onDone, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_banner.components.banner_edit_modal.title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              label={t('pages.application_banner.components.banner_edit_modal.team_label')}
              placeholder={t('pages.application_banner.components.banner_edit_modal.team_label')}
              showFlag
              prefetchOnOpen
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_banner.components.banner_edit_modal.title_label')}
              {...register('title')}
              helperText={t('pages.application_banner.components.banner_edit_modal.title_helper')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_banner.components.banner_edit_modal.link_label')}
              {...register('source_url')}
              helperText={t('pages.application_banner.components.banner_edit_modal.link_helper')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.source_url, errorMessage: errors?.source_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_banner.components.banner_edit_modal.image_label')}
                imageURL={item.image_url}
                onFileChange={file => setValue('file', file)}
                helpText={t('pages.application_banner.components.banner_edit_modal.image_helper')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>

            {formValues.is_scheduled && (
              <>
                <InputField
                  withoutFloating
                  label={t('pages.application_banner.components.banner_edit_modal.start_at_label')}
                  type='datetime-local'
                  fullDatePickerArea
                  min={formatDateToDateTimeLocalInputValue(new Date())}
                  {...register('start_at')}
                  rootProps={{ isInvalid: !!errors?.start_at, errorMessage: errors?.start_at?.message }}
                />
                <InputField
                  withoutFloating
                  label={t('pages.application_banner.components.banner_edit_modal.end_at_label')}
                  type='datetime-local'
                  fullDatePickerArea
                  min={formatDateToDateTimeLocalInputValue(new Date())}
                  {...register('end_at')}
                  rootProps={{ isInvalid: !!errors?.end_at, errorMessage: errors?.end_at?.message }}
                />
              </>
            )}

            <FormControl display='flex' gap={2} alignItems='center'>
              <Text fontWeight='medium'>{t('pages.application_banner.components.banner_edit_modal.partner_switch')}</Text>
              <Switch
                name='has_partner'
                isChecked={formValues.has_partner}
                onChange={event => {
                  setValue('has_partner', event.target.checked);
                }}
              />
            </FormControl>

            {formValues.has_partner && (
              <InputGroup display='flex' flexDirection='column' mb={4}>
                <Text fontWeight='medium'>{t('pages.application_banner.components.banner_edit_modal.partner_label')}</Text>
                <SelectField
                  {...register('partner_uuid')}
                  rootProps={{
                    isInvalid: !!errors?.partner_uuid,
                    errorMessage: errors?.partner_uuid?.message?.toString(),
                    mt: 0,
                  }}
                >
                  <option value=''>{t('pages.application_banner.components.banner_edit_modal.partner_placeholder')}</option>
                  {partners?.map(partner => (
                    <option key={partner.uuid} value={partner.uuid}>
                      {partner.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_banner.components.banner_edit_modal.cancel')}
            </Button>
            <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
              {t('pages.application_banner.components.banner_edit_modal.save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={updateBannerMutation.isLoading || isLoadingPartners} />
    </Modal>
  );
};
